import {
    getPortfolioGenerationPreviewPath,
    isPortfolioGenerationPreviewRoute
} from "@/scripts/view/portfolio/generationPreview.js";

const STORAGE_KEY_PREFIX = "uxfolio-ai-portfolio-preview:";

export function generatedPortfolioPreviewStorageKey(shortId) {
    return `${STORAGE_KEY_PREFIX}${shortId}`;
}

export function isTemplatePreviewRoute(route) {
    const preview = route?.query?.templatePreview;
    return preview === "true" || preview === true;
}

function isTracePreviewRoute(route) {
    const preview = route?.query?.tracePreview;
    return preview === "true" || preview === true;
}

export function isGeneratedPortfolioTemplatePreview(route) {
    return Boolean(route?.params?.shortId)
        && (isTemplatePreviewRoute(route) || isTracePreviewRoute(route));
}

export function isGeneratedProjectTemplatePreview(route) {
    return Boolean(route?.params?.portfolioShortId)
        && Boolean(route?.params?.projectShortId)
        && (isTemplatePreviewRoute(route) || isTracePreviewRoute(route));
}

export function storeGeneratedPortfolioPreview(shortId, payload) {
    if (!shortId || payload == null) {
        return;
    }

    sessionStorage.setItem(
        generatedPortfolioPreviewStorageKey(shortId),
        JSON.stringify({
            ...payload,
            loggedInUserId: null
        })
    );
}

// Inside the onboarding template preview, case study links stay inert. Inside the AI
// generator's preview — recognisable by its cached payload — they navigate, keeping the
// templatePreview flag so the target page hydrates from the same cache.
export function generatedPreviewNavigationTarget(route, portfolioShortId, path, projectShortId = null) {
    const previewQuery = isTracePreviewRoute(route)
        ? { tracePreview: "true" }
        : { templatePreview: "true" };

    if ((!isTemplatePreviewRoute(route)
        && !isTracePreviewRoute(route)
        && !isPortfolioGenerationPreviewRoute(route)) || !path) {
        return null;
    }

    // Durable generation previews load their payload from the backend, so they do
    // not have the session-storage cache used by Lab/template previews.
    if (isPortfolioGenerationPreviewRoute(route)) {
        const generationPreviewPath = path.startsWith("/portfolio-generation/")
            ? path
            : getPortfolioGenerationPreviewPath(route, projectShortId);
        return { path: generationPreviewPath || path, query: previewQuery };
    }

    if (!readGeneratedPortfolioPreview(portfolioShortId)) {
        return null;
    }

    return { path, query: previewQuery };
}

export function findGeneratedPreviewPage(payload, projectShortId) {
    if (!payload || !projectShortId) {
        return null;
    }

    const pages = [
        ...(Array.isArray(payload.portfolioPages) ? payload.portfolioPages : []),
        ...(Array.isArray(payload.caseStudies) ? payload.caseStudies : [])
    ];

    return pages.find((page) => page?.shortId === projectShortId) || null;
}

// The preview iframe reloads whenever the visitor switches page, so the palette and font
// pair they picked have to survive in the cache or the next page renders with the defaults.
export function updateGeneratedPortfolioPreviewDesignSettings(shortId, designSettings) {
    const payload = readGeneratedPortfolioPreview(shortId);
    if (!payload || !designSettings) {
        return;
    }

    const applyTo = (page) => (page ? { ...page, designSettings } : page);

    storeGeneratedPortfolioPreview(shortId, {
        ...payload,
        portfolio: payload.portfolio ? { ...payload.portfolio, designSettings } : payload.portfolio,
        project: applyTo(payload.project),
        portfolioPages: (payload.portfolioPages || []).map(applyTo),
        caseStudies: (payload.caseStudies || []).map(applyTo)
    });
}

export function readGeneratedPortfolioPreview(shortId) {
    if (!shortId) {
        return null;
    }

    const raw = sessionStorage.getItem(generatedPortfolioPreviewStorageKey(shortId));
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch (error) {
        return null;
    }
}
